/**
 * 送信中に画面が自動消灯しないようにする（Screen Wake Lock API）。
 *
 * 「他アプリを前面にしている間も送信を続ける」こと自体はブラウザ標準の
 * PWAでは実現できない（iOSは背景に回ると数秒でJS実行が止まり、Androidにも
 * 継続的な背景実行の手段が無い）。ただし実運用で最も多い停止原因は
 * 「ポケットの中で画面が消灯して止まる」ことなので、これは防げる。
 *
 * 未対応ブラウザ・許可が下りない場合は黙って諦める（送信自体は継続できる
 * ので、機能低下にとどめエラーにはしない）。
 */
export class ScreenWakeLockKeeper {
  #sentinel: WakeLockSentinel | null = null;
  #wanted = false;

  get active(): boolean {
    return this.#sentinel !== null;
  }

  async start(): Promise<void> {
    this.#wanted = true;
    await this.#acquire();
  }

  stop(): void {
    this.#wanted = false;
    const sentinel = this.#sentinel;
    this.#sentinel = null;
    void sentinel?.release();
  }

  /**
   * 画面が前面に戻ったときに呼ぶ。ロックは画面が背景に回ると仕様上
   * 自動解放されるため、`start()` 済みのまま戻ってきたら取り直す。
   */
  async reacquire(): Promise<void> {
    if (this.#wanted && !this.#sentinel) await this.#acquire();
  }

  async #acquire(): Promise<void> {
    if (!('wakeLock' in navigator)) return;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      if (!this.#wanted) {
        // 取得完了を待つ間に stop() されていたら、そのまま手放す
        void sentinel.release();
        return;
      }
      this.#sentinel = sentinel;
      sentinel.addEventListener('release', () => {
        if (this.#sentinel === sentinel) this.#sentinel = null;
      });
    } catch {
      // 未対応・拒否など。送信自体は継続できるので黙って諦める
    }
  }
}
